import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { showToast } from "../Buttons/Toaster";
import { ToastContainer, toast } from "react-toastify";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { prism } from "react-syntax-highlighter/dist/esm/styles/prism";
import CustomTooltip from "../Buttons/CustomTooltip";

const transparentStyle = {
  ...prism,
  'code[class*="language-"]': {
    ...prism['code[class*="language-"]'],
    background: "transparent",

    color: "white",
    fontFamily:
      '"SF NS Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "21px",
    letterSpacing: "0%",
    textShadow: "none",
  },
  'pre[class*="language-"]': {
    ...prism['pre[class*="language-"]'],
    background: "transparent",
    fontFamily:
      '"SF NS Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "21px",
    letterSpacing: "0%",
    padding: "5px",
    textShadow: "none",
  },
  // The base "prism" theme gives operators/entities/urls a translucent white
  // background - subtle on a light page, but a visible grey box on our dark
  // panel (e.g. every "<", ">", "=" in XML/SQL content). Drop it here.
  operator: { ...prism.operator, background: "transparent" },
  entity: { ...prism.entity, background: "transparent" },
  url: { ...prism.url, background: "transparent" },
};
const cellStyle = {
  textAlign: "left",
  verticalAlign: "middle",
};

// Prism's regex-based tokenizer scales very poorly on large inputs and can
// block the main thread for a long time. Above this size we skip syntax
// highlighting entirely and fall back to a plain <pre> preview instead.
const MAX_HIGHLIGHT_CHARS = 100000;
// Even the plain-text fallback shouldn't mount an unbounded amount of text
// into the DOM - cap what we actually render.
const MAX_PLAIN_PREVIEW_CHARS = 200000;

const plainPreviewStyle = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  color: "white",
  fontFamily:
    '"SF NS Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "21px",
  margin: 0,
};

// The backend doesn't always echo back a file's extension, and this used to
// unconditionally fall back to "sql" - which made Prism highlight non-SQL
// files (e.g. XML) using the SQL grammar, turning "<", ">" and "=" into SQL
// "operator" tokens. Those carry a translucent white background in the
// theme (meant to be subtle on a light page) that shows up as a visible grey
// box on our dark preview panel. Deriving the extension from the filename
// keeps the correct language/grammar so this mis-highlighting doesn't happen.
function getExtensionFromFilename(filename) {
  if (!filename || typeof filename !== "string") return null;
  const parts = filename.split(".");
  if (parts.length < 2) return null;
  const ext = parts.pop().toLowerCase();
  return ext === "py" ? "python" : ext;
}

// Renders file content, avoiding Prism's SyntaxHighlighter for very large
// files so uploading/previewing a big file doesn't freeze the whole tab.
function FileContentPreview({ content, language }) {
  const text = content || "";
  if (text.length <= MAX_HIGHLIGHT_CHARS) {
    return (
      <SyntaxHighlighter language={language} style={transparentStyle}>
        {text}
      </SyntaxHighlighter>
    );
  }

  const truncated = text.length > MAX_PLAIN_PREVIEW_CHARS;
  const preview = truncated ? text.slice(0, MAX_PLAIN_PREVIEW_CHARS) : text;

  return (
    <>
      <div className="font-size-10 text-warning mb-2">
        <i className="fas fa-exclamation-triangle me-2"></i>
        This file is large - showing a plain text preview to keep the UI
        responsive. The full file will still be uploaded and converted
        normally.
      </div>
      <pre style={plainPreviewStyle}>
        {preview}
        {truncated ? "\n\n… (preview truncated, full content will be processed)" : ""}
      </pre>
    </>
  );
}

function CreateModels({
  show,
  setShow,
  currentStep,
  setCurrentStep,
  onJobExecuted,
}) {
  // State declarations
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [jobName, setJobName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [selectedSourceDatabase, setSelectedSourceDatabase] = useState(null);
  const [selectedTargetDatabase, setSelectedTargetDatabase] = useState(null);
  const [availableSources, setAvailableSources] = useState([]);
  const [availableTargets, setAvailableTargets] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const [fileContent, setFileContent] = useState("");
  const [sourceType, setSourceType] = useState("upload");
  const [repoUrl, setRepoUrl] = useState("");

  const [indeterminateRows, setIndeterminateRows] = useState({});
  const [checkedRows, setCheckedRows] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [duplicateJobError, setDuplicateJobError] = useState(null);
  const [checkedFileId, setCheckedFileId] = useState(null);
  const [jobNameError, setJobNameError] = useState("");
  const [isProcessingStep, setIsProcessingStep] = useState(false);
  const dbGridRef = useRef(null);
  const [gridMaxHeight, setGridMaxHeight] = useState(null);
  const modalStyle = {
    width: currentStep === 6 ? "1039px" : "",
  };
  const calculateAvailableHeight = useCallback(() => {
    if (!show || (currentStep !== 2 && currentStep !== 3)) return;

    // Get modal container
    const modal = document.querySelector(".right-bar3");
    if (!modal) return;

    const modalRect = modal.getBoundingClientRect();

    // Calculate heights of all fixed elements
    const headerHeight = 60; // Title + trace label
    const stepInfoHeight = 80; // Step title + description
    const stepperHeight = 100;
    const jobInfoHeight = currentStep === 2 ? 100 : 150; // Job name/description area
    const buttonsHeight = 60; // Close/Next buttons

    // Calculate total fixed height
    const fixedElementsHeight =
      headerHeight +
      stepInfoHeight +
      stepperHeight +
      jobInfoHeight +
      buttonsHeight;

    // Calculate available space (viewport height minus modal top position minus fixed heights)
    const availableHeight =
      window.innerHeight - modalRect.top - fixedElementsHeight - 20; // 20px buffer

    // Set minimum and maximum limits
    const minHeight = 100; // At least 200px
    const maxHeight = 600; // Maximum 600px

    const calculatedHeight = Math.min(
      Math.max(availableHeight, minHeight),
      maxHeight,
    );

    setGridMaxHeight(calculatedHeight);
  }, [show, currentStep]);
  // Add this useEffect
  useEffect(() => {
    if (!show) return;

    // Calculate on step change
    calculateAvailableHeight();

    // Recalculate on window resize
    const handleResize = () => {
      calculateAvailableHeight();
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [show, currentStep, calculateAvailableHeight]);
  const getAllowedExtensions = useCallback(() => {
    if (!selectedSourceDatabase) return [];
    if (selectedSourceDatabase.toLowerCase() === "dbt") {
      return selectedTargetDatabase?.toLowerCase() === "aws glue"
        ? [".zip"]
        : [".sql"];
    }
    return databaseFileTypes[selectedSourceDatabase] || [];
  }, [selectedSourceDatabase, selectedTargetDatabase]);

  // Also recalculate when modal opens/closes
  useEffect(() => {
    if (show) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        calculateAvailableHeight();
      }, 100);
    }
  }, [show, calculateAvailableHeight]);
  const toggleRow = (rowId) => {
    console.log("rowId", rowId);
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };
  const handleCheckboxChange = (fileId, isFile = false) => {
    if (!isFile) return;

    // Create a new checked state with only this file selected
    const newChecked = {};
    Object.keys(checkedRows).forEach((key) => {
      newChecked[key] = false; // Uncheck all
    });
    newChecked[`file-${fileId}`] = true; // Check only the selected file

    setCheckedRows(newChecked);
    setSelectedIds([fileId]);
    setIsDeleteEnabled(true);
    setIsEditEnabled(true);
  };
  useEffect(() => {
    if (fileContent && fileContent.length > 0) {
      const firstFileId = fileContent[0].file_id;
      setCheckedRows({ [`file-${firstFileId}`]: true });
      setSelectedIds([firstFileId]);
      setIsDeleteEnabled(true);
      setIsEditEnabled(true);
    }
  }, [fileContent]);

  const toggleSelect = (filePath) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [filePath]: !prev[filePath],
    }));
  };
  useEffect(() => {
    setDuplicateJobError(null);
  }, [jobName]);
  useEffect(() => {
    console.log("FileContent:", fileContent);
  }, [fileContent]);

  useEffect(() => {
    if (currentStep === 6 && jobId) {
      setExpandedRows((prev) => ({
        ...prev,
        [jobId]: true, // Set to expanded by default
      }));
    }
  }, [currentStep, jobId]);
  // Database file types configuration
  const databaseFileTypes = {
    Snowflake: [".sql"],
    "Oracle T-SQL": [".sql"],
    "dbt (Redshift)": [".sql"],
    "Snowflake (Openflow)": [".sql"],
    Informatica: [".xml"],
    "Informatica BDM": [".xml"],
    PySpark: [".py"],
    "Spark SQL": [".sql"],
    "dbt (Databricks)": [".sql"],
    dbt: [".sql"],
    "Oracle Stored Procedure": [".sql"],
    PostgreSQL: [".sql"],
    Databricks: [".sql"],
    SQL: [".sql"],
    "SQL Server Stored Procedure": [".sql"],
    "MS SQL Server": [".sql"],
    "IBM Datastage": [".xml"],
    SSIS: [".xml", ".dtsx"],
    SAS: [".sas", ".flw", ".txt"],
    coalesce: ["yml"],
    "Redshift SQL": [".sql"],
    Redshift: [".sql"],
    "Snowflake (Scala)": [".scala"],
    "AWS Athena": [".sql"],
    "Databricks Lakebase": [".sql"],
    "PySpark (Standalone)": [".py"],
    "SAP HANA": [".sql"],
    MongoDB: [".json", ".json5"],
    "Starburst SQL": [".sql"],
    Netezza: [".sql"],
    "AWS Glue": [".zip"],
  };
  useEffect(() => {
    if (selectedSourceDatabase && selectedFiles.length > 0) {
      const allowedExtensions = getAllowedExtensions();
      const hasInvalidFiles = selectedFiles.some((file) => {
        const ext = `.${file.name.split(".").pop().toLowerCase()}`;
        return !allowedExtensions.includes(ext);
      });

      if (hasInvalidFiles) {
        setSelectedFiles([]);
        setUploadComplete(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        showToast(
          `Files cleared - new source database requires ${allowedExtensions.join(
            ", ",
          )} files`,
          "error",
        );
      }
    }
  }, [selectedSourceDatabase]);

  // Helper function to validate job name (no special characters)
  const validateJobName = (name) => {
    // Allows letters, numbers, spaces, hyphens, and underscores
    // return /^[a-zA-Z0-9 _-]+$/.test(name);
    return /^[a-zA-Z0-9 ]+$/.test(name);
  };

  // Helper function to limit text to 500 characters
  const limitDescription = (text) => {
    return text.length <= 500 ? text : text.substring(0, 500);
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;

    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Function to remove a file and reset input
  const removeFile = (index) => {
    setSelectedFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
    if (fileInputRef.current && selectedFiles.length === 1) {
      fileInputRef.current.value = "";
    }
    setUploadComplete(false);
  };

  // Button configuration for each step
  const getButtonConfig = () => {
    switch (currentStep) {
      case 1:
        return {
          primary: {
            text: "Next",
            onClick: handleNext1,
            // variant: "primary",
            className: "btn btn-primary custom-button",
            disabled: !jobName.trim(),
          },
        };
      case 2:
        return {
          secondary: {
            text: "Previous",
            onClick: () => updateStep(currentStep - 1),
            // variant: "light"
            className: "btn btn-closebtn custom-button",
          },
          primary: {
            text: "Next",
            onClick: handleNext,
            // variant: "primary",
            className: "btn btn-primary custom-button",
            disabled: !selectedSourceDatabase,
          },
        };
      case 3:
        return {
          secondary: {
            text: "Previous",
            onClick: () => updateStep(currentStep - 1),
            // variant: "light"
            className: "btn btn-closebtn custom-button",
          },
          primary: {
            text: "Next",
            onClick: handleNext,
            // variant: "primary",
            className: "btn btn-primary custom-button",
            disabled: !selectedTargetDatabase,
          },
        };
      case 4:
        return {
          secondary: {
            text: "Previous",
            onClick: () => updateStep(currentStep - 1),
            // variant: "light"
            className: "btn btn-closebtn custom-button",
          },
          primary: {
            text: "Next",
            onClick: handleNext,
            // variant: "primary",
            className: "btn btn-primary custom-button",
            disabled: false,
          },
        };
      case 5:
        if (sourceType === "upload") {
          const shouldShowNext = uploadComplete && uploadedFiles.length > 0;
          return {
            secondary: {
              text: "Previous",
              onClick: () => updateStep(currentStep - 1),
              className: "btn btn-closebtn custom-button",
            },
            primary: {
              text: shouldShowNext
                ? isProcessingStep
                  ? "Processing..."
                  : "Next"
                : "Upload",
              onClick: shouldShowNext ? handleUploadNext : handleFileUpload,
              className: "btn btn-primary custom-button",
              disabled:
                (selectedFiles.length === 0 && !shouldShowNext) ||
                isUploading ||
                isProcessingStep,
            },
          };
        } else {
          return {
            secondary: {
              text: "Previous",
              onClick: () => updateStep(currentStep - 1),
              className: "btn btn-closebtn custom-button",
            },
            primary: {
              text: isProcessingStep ? "Processing..." : "Next",
              onClick: handleGitHubNext,
              className: "btn btn-primary custom-button",
              disabled: !repoUrl.trim() || isProcessingStep,
            },
          };
        }
      case 6:
        return {
          secondary: {
            text: "Skip and Do Later",
            onClick: handleNext,
            className: "btn btn-closebtn custom-button",
            // variant: "light"
          },
          primary: {
            text: isExecuting ? "Executing" : "Start Execution",
            onClick: startExecution,
            // variant: "primary",
            className: "btn btn-primary custom-button",
            disabled: isExecuting,
          },
        };
      default:
        return {
          primary: {
            text: "Next",
            onClick: handleNext,
            // variant: "primary"
            className: "btn btn-primary custom-button",
          },
        };
    }
  };

  // Validate step number
  const isValidStep = (step) => step >= 1 && step <= 6;

  // Format a Date to match the backend's "DD-MM-YYYY HH24:MI:SS" display format
  const formatTimestamp = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(
      date.getHours(),
    )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  // Build a client-side preview of the job so it can appear in the table
  // immediately, without waiting on the websocket's next polling cycle.
  const buildJobPreview = () => {
    const now = formatTimestamp(new Date());
    const files = Array.isArray(fileContent)
      ? fileContent.map((file) => ({
          file_id: file.file_id,
          file_name: file.raw_file_name || file.file_name,
          status: "In Progress",
          file_created_at: now,
          file_completed_at: null,
        }))
      : [];

    return {
      job_id: jobId,
      job_name: jobName,
      description: jobDescription,
      source_db: selectedSourceDatabase,
      target_db: selectedTargetDatabase,
      status: "In Progress",
      created_at: now,
      completed_at: null,
      files,
      file_count: files.length,
    };
  };

  // Start job execution
  const startExecution = async () => {
    if (isExecuting) return;

    handleCloseExcecute();
    setIsExecuting(true);
    if (onJobExecuted) {
      onJobExecuted();
    }
    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        showToast("Authentication token missing", "error");
        setIsExecuting(false);
        return;
      }

      if (!jobId) {
        showToast("Job ID is missing", "error");
        setIsExecuting(false);
        return;
      }
      if (onJobExecuted) {
        // Pass the job preview along so the table can show it right away,
        // instead of waiting for the next websocket poll to confirm it.
        onJobExecuted(jobId, buildJobPreview());
      }
      const apiBase = process.env.REACT_APP_API_URL_CODE_CONVERSION;
      const response = await fetch(`${apiBase}/execute?job_ids=${jobId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Execution failed", "error");
        if (onJobExecuted) onJobExecuted(jobId, "error");
        setIsExecuting(false);
        return;
      }

      const result = await response.json();
      showToast(`Conversion completed for job: ${jobName}`, "success");
      if (onJobExecuted) {
        onJobExecuted();
      }
    } catch (error) {
      console.error("Execution error:", error);
      if (onJobExecuted) onJobExecuted(jobId, "error");
      showToast("Failed to start execution", "error");
      setIsExecuting(false);
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (!selectedSourceDatabase) {
      showToast("Please select a source database first", "error");
      event.target.value = "";
      return;
    }

    const allowedExtensions = getAllowedExtensions();
    const newValidFiles = Array.from(files).filter((file) => {
      const ext = `.${file.name.split(".").pop().toLowerCase()}`;
      return allowedExtensions.includes(ext);
    });

    if (newValidFiles.length !== files.length) {
      showToast(
        `Only ${allowedExtensions.join(
          ", ",
        )} files are allowed for ${selectedSourceDatabase}`,
        "error",
      );
      event.target.value = "";
      return;
    }

    setSelectedFiles((prevFiles) => {
      const existingNames = new Set(prevFiles.map((f) => f.name));
      const uniqueNewFiles = newValidFiles.filter(
        (file) => !existingNames.has(file.name),
      );
      return [...prevFiles, ...uniqueNewFiles];
    });

    setUploadComplete(false);
    setUploadProgress(0);
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const dataTransfer = new DataTransfer();
      Array.from(files).forEach((file) => dataTransfer.items.add(file));
      fileInputRef.current.files = dataTransfer.files;
      const event = new Event("change", { bubbles: true });
      fileInputRef.current.dispatchEvent(event);
      setUploadComplete(false);
      setUploadProgress(0);
    }
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (typeof bytes !== "number") return "N/A";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Handle modal close
  const handleClose = async (shouldSaveDraft = true) => {
    if (shouldSaveDraft && jobId) {
      await saveAsDraft(jobId);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setDuplicateJobError(null);
    setShow(false);
    setTimeout(() => {
      setCurrentStep(1);
      setJobName("");
      setJobDescription("");
      setSelectedSourceDatabase(null);
      setSelectedTargetDatabase(null);
      setUploadedFiles([]);
      setSelectedFile(null);
      setSelectedFiles([]);
      setUploadComplete(false);
      setJobId(null);
      setIsUploading(false);
      setUploadProgress(0);
      setIsExecuting(false);
      setRepoUrl("");
      setSourceType("upload");
      // Drop any previewed file content (can hold large strings for big
      // uploads) so it doesn't linger in memory for the rest of the session.
      setFileContent("");
      setCheckedRows({});
      setExpandedRows({});
      setIndeterminateRows({});
    }, 300);
  };

  const handleCloseExcecute = async () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setDuplicateJobError(null);
    setShow(false);
    setTimeout(() => {
      setCurrentStep(1);
      setJobName("");
      setJobDescription("");
      setSelectedSourceDatabase(null);
      setSelectedTargetDatabase(null);
      setUploadedFiles([]);
      setSelectedFile(null);
      setSelectedFiles([]);
      setUploadComplete(false);
      setJobId(null);
      setIsUploading(false);
      setUploadProgress(0);
      setIsExecuting(false);
      setRepoUrl("");
      setSourceType("upload");
      // Drop any previewed file content (can hold large strings for big
      // uploads) so it doesn't linger in memory for the rest of the session.
      setFileContent("");
      setCheckedRows({});
      setExpandedRows({});
      setIndeterminateRows({});
    }, 300);
  };
  useEffect(() => {
    if (selectedSourceDatabase && uploadedFiles.length > 0) {
      const compatibleFiles = checkFilesCompatibility(uploadedFiles);
      if (compatibleFiles.length !== uploadedFiles.length) {
        setUploadedFiles(compatibleFiles);
        if (compatibleFiles.length === 0) {
          setUploadComplete(false);
        }
        showToast(
          `Incompatible files removed for ${selectedSourceDatabase}`,
          "warning",
        );
      }
    }
  }, [selectedSourceDatabase]);
  // Update step with validation
  const updateStep = (step) => {
    if (!isValidStep(step)) return;

    if (
      step < currentStep &&
      (currentStep === 4 || currentStep === 5 || currentStep === 6) &&
      step === 2
    ) {
      // Clear any incompatible files if source database changes
      const newSourceDb = selectedSourceDatabase; // This would be the new selection
      if (newSourceDb && uploadedFiles.length > 0) {
        const compatibleFiles = checkFilesCompatibility(uploadedFiles);
        if (compatibleFiles.length !== uploadedFiles.length) {
          setUploadedFiles(compatibleFiles);
          if (compatibleFiles.length === 0) {
            setUploadComplete(false);
          }
          showToast(`Incompatible files removed for ${newSourceDb}`, "warning");
        }
      }
    }
    if (step > currentStep) {
      if (currentStep === 1 && !jobName.trim()) {
        showToast("Please enter a job name ", "error");
        return;
      }
      if (currentStep === 2 && !selectedSourceDatabase) {
        showToast("Please select a source database", "error");
        return;
      }
      if (currentStep === 3 && !selectedTargetDatabase) {
        showToast("Please select a target database", "error");
        return;
      }
      if (
        currentStep === 5 &&
        sourceType === "upload" &&
        selectedFiles.length === 0 &&
        uploadedFiles.length === 0
      ) {
        showToast("Please select at least one file", "error");
        return;
      }
    }

    setCurrentStep(step);
  };

  // Handle file upload
  const handleFileUpload = () => {
    if (selectedFiles.length === 0) {
      showToast("Please select at least one file", "error");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const interval = 50;
    const totalSteps = 4000 / interval;
    let currentStep = 0;

    const progressInterval = setInterval(() => {
      currentStep++;
      const progress = Math.min((currentStep / totalSteps) * 100, 100);
      setUploadProgress(progress);

      if (currentStep >= totalSteps) {
        clearInterval(progressInterval);
        setIsUploading(false);
        setUploadComplete(true);

        setUploadedFiles((prev) => [...prev, ...selectedFiles]);

        setTimeout(() => {
          setSelectedFiles([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }, 500);
      }
    }, interval);
  };

  const handleGitHubNext = async () => {
    if (!repoUrl.trim()) {
      showToast("Please enter a repository URL", "error");
      return;
    }

    setIsProcessingStep(true);

    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        showToast("Authentication token missing", "error");
        return;
      }

      const apiBasecode = process.env.REACT_APP_API_URL_CODE_CONVERSION;
      const url = new URL(`${apiBasecode}/github-pull`);

      const formData = new FormData();
      formData.append("job_name", jobName);
      formData.append("job_description", jobDescription);
      formData.append("source_db", selectedSourceDatabase);
      formData.append("target_db", selectedTargetDatabase);
      formData.append("repo_url", repoUrl);

      const response = await fetch(url.toString(), {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (error.detail === "A job with this name already exists.") {
          setDuplicateJobError(
            "A job with this name already exists. Choose a different name.",
          );
        } else {
          showToast(error.detail || "Failed to pull from GitHub", "error");
        }
        return;
      }

      const result = await response.json();
      showToast(
        result.message || "Successfully pulled from GitHub!",
        "success",
      );
      setDuplicateJobError(null);
      setJobId(result.job_id);

      // Flatten input_files for dbt migrations
      const processedFiles = [];
      if (Array.isArray(result.files)) {
        result.files.forEach((file) => {
          if (Array.isArray(file.input_files) && file.input_files.length > 0) {
            file.input_files.forEach((folder, folderIdx) => {
              if (Array.isArray(folder.files)) {
                folder.files.forEach((nestedFile, fileIdx) => {
                  processedFiles.push({
                    ...nestedFile,
                    file_id: `${file.file_id}-${folderIdx}-${fileIdx}`,
                    raw_file_name: nestedFile.file_name,
                    raw_file_content: nestedFile.file_content,
                    raw_file_extension:
                      nestedFile.file_extension ||
                      getExtensionFromFilename(nestedFile.file_name) ||
                      "sql",
                  });
                });
              }
            });
          } else {
            processedFiles.push({
              ...file,
              raw_file_name: file.file_name,
              raw_file_extension:
                file.raw_file_extension ||
                getExtensionFromFilename(file.file_name) ||
                "sql",
            });
          }
        });
      }
      setFileContent(processedFiles);
      updateStep(currentStep + 1);
    } catch (error) {
      console.error("API error:", error);
      showToast("Failed to create job from GitHub repository", "error");
    } finally {
      setIsProcessingStep(false);
    }
  };

  const handleUploadNext = async () => {
    if (uploadedFiles.length === 0) {
      showToast("Please upload your files first", "error");
      return;
    }

    setIsProcessingStep(true);

    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        showToast("Authentication token missing", "error");
        return;
      }

      const apiBasecode = process.env.REACT_APP_API_URL_CODE_CONVERSION;
      const url = new URL(`${apiBasecode}/create_job`);
      url.searchParams.append("job_name", jobName);
      url.searchParams.append("job_description", jobDescription);
      url.searchParams.append("source_db", selectedSourceDatabase);
      url.searchParams.append("target_db", selectedTargetDatabase);

      const formData = new FormData();
      uploadedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(url.toString(), {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.log(error, "priya");
        if (error.detail === "A job with this name already exists.") {
          console.log(error.detail, "pooja");
          setDuplicateJobError(
            "A job with this name already exists. Choose a different name.",
          );
          return;
        }
        showToast(error.message || error.detail, "error");
        return;
      } else {
        const result = await response.json();
        if (result.message.includes("Warning")) {
          showToast(
            result.message ||
              "Warning! input script file size exceeds 5000 lines which may take several minutes to complete. Reach out to DBShift Admin in case you see any stalled job or screen timeout.",
            "warning",
          );
        } else {
          showToast(result.message || "Job created successfully!", "success");
        }
        setDuplicateJobError(null);
        setJobId(result.job_id);
        updateStep(currentStep + 1);

        // Flatten input_files for dbt migrations
        const processedFiles = [];
        if (Array.isArray(result.files)) {
          result.files.forEach((file) => {
            if (Array.isArray(file.input_files) && file.input_files.length > 0) {
              file.input_files.forEach((folder, folderIdx) => {
                if (Array.isArray(folder.files)) {
                  folder.files.forEach((nestedFile, fileIdx) => {
                    processedFiles.push({
                      ...nestedFile,
                      file_id: `${file.file_id}-${folderIdx}-${fileIdx}`,
                      raw_file_name: nestedFile.file_name,
                      raw_file_content: nestedFile.file_content,
                      raw_file_extension:
                        nestedFile.file_extension ||
                        getExtensionFromFilename(nestedFile.file_name) ||
                        "sql",
                    });
                  });
                }
              });
            } else {
              processedFiles.push({
                ...file,
                raw_file_name: file.file_name,
                raw_file_extension:
                  file.raw_file_extension ||
                  getExtensionFromFilename(file.file_name) ||
                  "sql",
              });
            }
          });
        }
        setFileContent(processedFiles);
      }
    } catch (error) {
      console.error("API error:", error);
      showToast("Failed to create job and upload files", "error");
    } finally {
      setIsProcessingStep(false);
    }
  };

  // Handle step 1 next action
  const handleNext1 = () => {
    if (!jobName.trim()) {
      showToast("Please enter a job name ", "error");
      return;
    }
    fetchDatabaseDetails();
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const saveAsDraft = async (jobId) => {
    const apiBaseconversion = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        showToast("Authentication token missing", "error");
        return false;
      }

      const response = await axios.put(
        `${apiBaseconversion}/draft_job?job_id=${jobId}`,
        null,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      if (response.status === 200) {
        showToast("Job saved to draft", "success");
        return true;
      }
    } catch (error) {
      console.error("Failed to save draft:", error);
      showToast("Failed to save job to draft", "error");
      return false;
    }
  };

  // Fetch database details
  const fetchDatabaseDetails = async () => {
    const apiBasedata = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    const access_token = sessionStorage.getItem("access_token");
    if (!access_token) {
      showToast("Authentication required", "error");
      return null;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(`${apiBasedata}/get_platforms`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      if (response.data) {
        setAvailableSources(Object.keys(response.data));
        setAvailableTargets(response.data);
      }
    } catch (error) {
      console.error("Error fetching databases:", error);
      showToast("Failed to load database options", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle next step
  const handleNext = () => {
    updateStep(currentStep + 1);
  };

  const getDatabaseImagePath = useCallback((dbName) => {
    const normalized = dbName
      ? String(dbName).trim().toLowerCase().replace(/\s+/g, "-")
      : "";
    return `assets/images/${normalized}.png`;
  }, []);
  const getCustomImageStyle = (rawDbName) => {
    const dbName = rawDbName ? String(rawDbName).trim() : "";
    if (dbName === "PySpark" || dbName === "PySpark (Standalone)") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "Oracle Stored Procedure") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "coalesce") {
      console.log("Coalesce matched!"); // Debug log
      return {
        width: "70px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "Oracle T-SQL") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "Spark SQL") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "SQL Server Stored Procedure") {
      return {
        width: "",
        height: "",
        objectFit: "contain",
      };
    } else if (dbName === "MS SQL Server") {
      return {
        width: "",
        height: "",
        objectFit: "contain",
      };
    } else if (dbName === "dbt Redshift") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "Databricks Lakebase") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "MongoDB") {
      return {
        width: "65px",
        height: "45px",
        objectFit: "contain",
        maxHeight: "none",
      };
    } else if (dbName === "SAP HANA") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
      };
    } else if (dbName === "Starburst SQL") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
        maxHeight: "none",
      };
    } else if (dbName === "Netezza") {
      return {
        width: "59px",
        height: "40px",
        objectFit: "contain",
        maxHeight: "none",
      };
    } else if (dbName === "AWS Glue") {
      return {
        width: "65px",

        maxHeight: "70px",
        maxWidth: "100px",
        objectFit: "contain",
      };
    }
    return {
      width: "30px",
      height: "27px",
      objectFit: "contain",
    };
  };
  // Step information configuration
  const stepInfo = {
    1: {
      title: "Name Your Job",
      description: "Give a name and a description for your job",
      content: (
        <>
          <h6 className="fw-sbold font-size-12">
            Job Name <span style={{ color: "red" }}>*</span>
          </h6>
          <input
            type="text"
            placeholder="Enter job name"
            className={`form-control highlighted-input  font-size-12 doc-search mt-2 mb-3 colorfor-placeholder styled-input ${
              duplicateJobError || jobNameError ? "is-invalid" : ""
            }`}
            style={jobNameError ? { border: "1px solid #dc3545" } : {}}
            value={jobName}
            onChange={(e) => {
              const newName = capitalizeFirstLetter(e.target.value);
              if (validateJobName(newName) || newName === "") {
                setJobName(newName);
                setJobNameError("");
              } else {
                setJobNameError(
                  "Only letters, numbers, and spaces are allowed.",
                );
              }
            }}
          />
          {jobNameError && (
            <div
              className="text-danger font-size-10"
              style={{ marginTop: "-14px" }}
            >
              {jobNameError}
            </div>
          )}

          <h6 className="fw-sbold font-size-12 mt-2">Job Description</h6>
          <textarea
            rows="5"
            placeholder="Description"
            className="form-control highlighted-input font-size-12 doc-search mt-2 mb-3 capitalize-first colorfor-placeholder styled-input"
            style={{ height: "80px" }}
            value={jobDescription}
            onChange={(e) => {
              const limitedText = limitDescription(e.target.value);
              setJobDescription(capitalizeFirstLetter(limitedText));
            }}
          ></textarea>
          <div
            className="d-flex flex-justify-start font-size-10 text-muted text-end"
            style={{ marginTop: "-12px" }}
          >
            {jobDescription.length}/500 characters
          </div>
        </>
      ),
    },
    2: {
      title: "Select your Sources",
      description: "Select the destination from the list to proceed",
      content: <></>,
    },
    3: {
      title: "Select Target",
      description: "Select the target database for conversion",
      content: <></>,
    },
    // In the stepInfo object, update step 4 content:
    4: {
      title: "Choose Code Source",
      description: "Choose how you want to provide the code for conversion",
      content: (
        <>
          <div className="mb-3">
            <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
              {/* Job Name */}
              <div className="flex-grow-1" style={{ Width: "167px" }}>
                <h6 className="fw-sbold font-size-12 mb-0">Job Name</h6>
                <p className="font-size-12 text-dark mb-0">
                  {jobName || "N/A"}
                </p>
              </div>
              {/* Source DB */}
              <div className="flex-grow-1" style={{ Width: "152px" }}>
                <h6 className="fw-sbold font-size-12 mb-0">Source DB</h6>
                <p className="font-size-12 text-dark mb-0">
                  {selectedSourceDatabase || "Not specified"}
                </p>
              </div>
              {/* Target DB */}
              <div className="flex-grow-1" style={{ Width: "152px" }}>
                <h6 className="fw-sbold font-size-12 mb-0">Target DB</h6>
                <p className="font-size-12 text-dark mb-0">
                  {selectedTargetDatabase || "Not specified"}
                </p>
              </div>
            </div>

            <div className="d-flex flex-wrap mt-3" style={{ gap: "20px" }}>
              {/* Job Description */}
              <div
                className="flex-grow-1"
                style={{ minWidth: "200px", textAlign: "justify" }}
              >
                <h6 className="fw-sbold font-size-12 mb-0">Job Description</h6>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    textAlign: "justify",
                    maxHeight: "100px",
                  }}
                >
                  {jobDescription?.split("\n").map((line, i) => (
                    <p key={i} className="font-size-12 text-dark mb-0">
                      {line || (i === 0 ? "N/A" : "")}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Source Type Selection */}
          <h6 className="fw-sbold font-size-12 ">Source Type</h6>
          <div className="d-flex justify-content-start  ">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="sourceType"
                id="uploadRadio"
                value="upload"
                checked={sourceType === "upload"}
                onChange={() => setSourceType("upload")}
              />
              <label
                className="form-check-label font-size-12"
                htmlFor="uploadRadio"
              >
                Upload Local Files
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="sourceType"
                id="githubRadio"
                value="github"
                checked={sourceType === "github"}
                onChange={() => setSourceType("github")}
              />
              <label
                className="form-check-label font-size-12"
                htmlFor="githubRadio"
              >
                GitHub Upload
              </label>
            </div>
          </div>
        </>
      ),
    },
    5: {
      title: sourceType === "upload" ? "Upload Documents" : "GitHub Repository",
      description:
        sourceType === "upload"
          ? "Upload documents for database conversion"
          : "Enter your GitHub repository URL to pull files",
      content:
        sourceType === "upload" ? (
          <>
            <div className="mb-4">
              {duplicateJobError && (
                <div className="text-danger font-size-10 mb-2 d-flex align-items-center">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {duplicateJobError}
                </div>
              )}
              <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                {/* Job Name */}

                <div className="flex-grow-1" style={{ Width: "167px" }}>
                  <h6 className="fw-sbold font-size-12 mb-0">Job Name</h6>
                  <p className="font-size-12 text-dark mb-0">
                    {jobName || "N/A"}
                  </p>
                </div>
                {/* Source DB */}
                <div className="flex-grow-1" style={{ Width: "152px" }}>
                  <h6 className="fw-sbold font-size-12 mb-0">Source DB</h6>
                  <p className="font-size-12 text-dark mb-0">
                    {selectedSourceDatabase || "Not specified"}
                  </p>
                </div>
                {/* Target DB */}
                <div className="flex-grow-1" style={{ Width: "152px" }}>
                  <h6 className="fw-sbold font-size-12 mb-0">Target DB</h6>
                  <p className="font-size-12 text-dark mb-0">
                    {selectedTargetDatabase || "Not specified"}
                  </p>
                </div>
              </div>

              <div className="d-flex flex-wrap mt-3" style={{ gap: "20px" }}>
                {/* Job Description */}
                <div
                  className="flex-grow-1"
                  style={{ minWidth: "200px", textAlign: "justify" }}
                >
                  <h6 className="fw-sbold font-size-12 mb-0">
                    Job Description
                  </h6>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      textAlign: "justify", // Added justify text
                      maxHeight: "100px",
                      // overflowY: 'auto'
                    }}
                  >
                    {jobDescription?.split("\n").map((line, i) => (
                      <p key={i} className="font-size-12 text-dark mb-0">
                        {line || (i === 0 ? "N/A" : "")}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Section */}
            {selectedSourceDatabase?.toLowerCase() === "dbt" &&
              selectedTargetDatabase?.toLowerCase() === "aws glue" && (
                <div className="d-flex justify-content-end mb-2">
                  <div
                    className="alert alert-info font-size-10 py-1 px-2 mb-0 d-flex align-items-center"
                    style={{
                      width: "fit-content",
                      borderRadius: "4px",
                      border: "1px solid #B6E0FE",
                      backgroundColor: "#F0F9FF",
                      color: "#004085",
                    }}
                  >
                    <i className="fas fa-info-circle me-2"></i>
                    For DBT to AWS Glue, the supported file format is zip.
                  </div>
                </div>
              )}
            <div className="mb-4">
              <h6 className="fw-sbold font-size-12 mb-2">Upload Documents</h6>
              <div
                className="upload-file p-3 mt-2 position-relative"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                style={{
                  border: "2px dashed #ccc",
                  minHeight: "150px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="file"
                  id="file-upload"
                  ref={fileInputRef}
                  className="position-absolute w-100 h-100 opacity-0"
                  onChange={handleFileChange}
                  multiple
                  accept={
                    selectedSourceDatabase
                      ? getAllowedExtensions().join(",")
                      : ".sql,.xml,.py"
                  }
                  style={{ cursor: "pointer" }}
                />
                <div className="text-center">
                  <img
                    src="assets/images/upload.gif"
                    className="w-100px"
                    alt="Upload icon"
                  />
                  <h6 className="text-dark fw-medium text-center mb-1">
                    Drag and Drop files here or click to browse
                  </h6>
                  <p className="font-size-10 text-center mb-2">
                    Supported files for {selectedSourceDatabase}:
                    <span style={{ fontSize: "12px", fontWeight: "500" }}>
                      {getAllowedExtensions().join(", ")}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Upload Progress */}
            {!uploadComplete && selectedFiles.length > 0 && (
              <ul className="list-unstyled">
                {selectedFiles.map((file, index) => (
                  <li key={index} className="font-size-12 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-start font-size-10 ext-dark mt-2 mb-1">
                        {file.name} ({formatFileSize(file.size)})
                      </span>
                      <span
                        className="close-icon font-size-12 cursor-pointer"
                        onClick={() => removeFile(index)}
                      >
                        ×
                      </span>
                    </div>
                    <div className="progress custom-progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {uploadComplete && uploadedFiles.length > 0 && (
              <div className="mt-3">
                <h6 className="fw-sbold font-size-12">Uploaded Files</h6>
                <ul className="ps-1 mb-2 list-unstyled">
                  {uploadedFiles.map((file, index) => (
                    <li key={`uploaded-${index}`} className="font-size-12 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span>
                          {file.name} ({formatFileSize(file.size)})
                        </span>
                        <span
                          className="close-icon font-size-12 cursor-pointer"
                          onClick={() => {
                            setUploadedFiles((prev) =>
                              prev.filter((_, i) => i !== index),
                            );
                            if (
                              uploadedFiles.length === 1 &&
                              fileInputRef.current
                            ) {
                              fileInputRef.current.value = "";
                            }
                            setUploadComplete(uploadedFiles.length > 1);
                          }}
                        >
                          ×
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <>
            {duplicateJobError && (
              <div className="text-danger font-size-10 mb-2 d-flex align-items-center">
                <i className="fas fa-exclamation-circle me-2"></i>
                {duplicateJobError}
              </div>
            )}
            <h6 className="fw-sbold font-size-12">
              Repository URL <span style={{ color: "red" }}>*</span>
            </h6>
            <input
              type="text"
              placeholder="Enter repository URL"
              className="form-control highlighted-input font-size-12 doc-search mt-2 mb-3 colorfor-placeholder styled-input"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
            />
          </>
        ),
    },
    6: {
      title: "Database Files & Start",
      description: "Review uploaded files and start the conversion process",
      content: (
        <>
          <div className="mb-3">
            <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
              {/* Job Name */}
              <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                <h6 className="fw-sbold font-size-12 mb-0">Job Name</h6>
                <p className="font-size-12 text-dark mb-0">
                  {jobName || "N/A"}
                </p>
              </div>
              {/* Source DB */}
              <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                <h6 className="fw-sbold font-size-12 mb-0">Source DB</h6>
                <p className="font-size-12 text-dark mb-0">
                  {selectedSourceDatabase || "Not specified"}
                </p>
              </div>
              {/* Target DB */}
              <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                <h6 className="fw-sbold font-size-12 mb-0">Target DB</h6>
                <p className="font-size-12 text-dark mb-0">
                  {selectedTargetDatabase || "Not specified"}
                </p>
              </div>
            </div>

            <div className="d-flex flex-wrap mt-3" style={{ gap: "20px" }}>
              {/* Job Description */}
              <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                <h6 className="fw-sbold font-size-12 mb-0">Job Description</h6>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    textAlign: "justify", // Added justify text
                    maxHeight: "100px",
                    overflowY: "auto",
                  }}
                >
                  {jobDescription?.split("\n").map((line, i) => (
                    <p key={i} className="font-size-12 text-dark mb-0">
                      {line || (i === 0 ? "N/A" : "")}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            {duplicateJobError && (
              <div className="text-danger font-size-10 mt-2 d-flex align-items-center">
                <i className="fas fa-exclamation-circle me-2"></i>
                {duplicateJobError}
              </div>
            )}
          </div>

          <div className="d-flex flex-column mt-4">
            {/* Header row with "Uploaded Documents" and instruction text */}
            <div
              className="d-flex justify-content-between align-items-center mb-2"
              style={{ marginRight: "26px" }}
            >
              <h6 className="fw-sbold font-size-12 mb-0">
                {sourceType === "upload" ? "Uploaded" : "Pulled"} Documents
              </h6>
              <div
                className="font-size-12 text-muted text-end"
                style={{ maxWidth: "80%", marginBottom: "-7px" }}
              >
                <i
                  className="fas fa-info-circle me-1"
                  style={{ color: "#6c757d" }}
                ></i>
                When you click 'Start Execution', it will run all the files in
                the uploaded scripts. The view area is only for viewing the
                scripts.
              </div>
            </div>

            {/* Content area with scripts and file viewer */}
            <div className="upload-dco-view justify-content-between">
              {/* Left Side: Database Scripts */}
              <div
                className="p-2 mb-3 data-script"
                style={{
                  width: "265px",
                  backgroundColor: "#EFF1F3",
                  borderRadius: "5px",
                  overflowY: "auto",
                  minHeight: "200px",
                }}
              >
                <div
                  className="bg-white p-3"
                  style={{
                    borderRadius: "5px",
                    minHeight: "400px",
                    maxHeight: "1085px",
                  }}
                >
                  <p className="fw-sbold font-size-12 text-dark mb-3">
                    Database Scripts
                  </p>

                  {/* Job Dropdown */}
                  <div style={{ position: "relative", marginTop: "-17px" }}>
                    {/* Main job item with expand button */}
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-link p-0 me-2"
                        onClick={() => toggleRow(jobId)}
                        style={{
                          minWidth: "12px",
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                          backgroundColor: "transparent",
                          color: "grey",
                          marginBottom: "3px",
                        }}
                      >
                        <i
                          className={`fas ${
                            expandedRows[jobId]
                              ? "fa-chevron-down"
                              : "fa-chevron-right"
                          } font-size-10`}
                        ></i>
                      </button>
                      <label
                        className="font-size-12 user-select-none"
                        style={{
                          marginTop: "13px",
                          marginLeft: "2px",
                          backgroundColor: "transparent",
                          cursor: "pointer",
                        }}
                        onClick={() => toggleRow(jobId)}
                      >
                        {jobName}
                      </label>
                    </div>

                    {/* File List - shown when expanded */}
                    {expandedRows[jobId] && (
                      <div className="ps-4" style={{ position: "relative" }}>
                        {Array.isArray(fileContent) &&
                        fileContent.length > 0 ? (
                          fileContent.map((file, index) => (
                            <div
                              key={file.file_id}
                              className="d-flex align-items-center ps-3"
                              style={{
                                position: "relative",
                                marginTop: index === 0 ? "0" : "4px",
                              }}
                            >
                              {/* Individual curved connector for each file */}
                              <div
                                style={{
                                  position: "absolute",
                                  left: "6px",
                                  top: "-6px",
                                  height: "12px",
                                  width: "12px",
                                  borderLeft: "1px solid #888",
                                  borderBottom: "1px solid #888",
                                  borderBottomLeftRadius: "6px",
                                }}
                              ></div>

                              <div className="form-check d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input me-2"
                                  id={`file-checkbox-${file.file_id}`}
                                  checked={
                                    checkedRows[`file-${file.file_id}`] || false
                                  }
                                  onChange={() =>
                                    handleCheckboxChange(file.file_id, true)
                                  }
                                  style={{
                                    marginLeft: "-16px",
                                    marginTop: "-16px",
                                    boxShadow: "none",
                                    outline: "none",
                                    cursor: "pointer",
                                    borderRadius: "2px",
                                    color: "grey",
                                  }}
                                />

                                <CustomTooltip
                                  content={file.raw_file_name}
                                  placement="top"
                                  fallbackPlacements={["bottom"]}
                                  className="step-5-tooltip"
                                >
                                  <label
                                    className="font-size-12 text-truncate user-select-none"
                                    htmlFor={`file-checkbox-${file.file_id}`}
                                    style={{
                                      maxWidth: "160px",
                                      backgroundColor: "transparent",
                                      paddingLeft: "0px",
                                      cursor: "pointer",
                                      display: "inline-block",
                                    }}
                                  >
                                    {file.raw_file_name.length > 15
                                      ? `${file.raw_file_name.substring(0, 15)}...`
                                      : file.raw_file_name}
                                  </label>
                                </CustomTooltip>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="font-size-12 text-muted ps-3">
                            No files available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side: File Content */}
              <div
                className="text-white file-content p-3"
                style={{
                  backgroundColor: "#1C2535",
                  borderRadius: "5px",
                  minHeight: "400px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {Array.isArray(fileContent) && fileContent.length > 0 ? (
                  fileContent.map(
                    (file) =>
                      checkedRows[`file-${file.file_id}`] && (
                        <div key={file.file_id}>
                          <FileContentPreview
                            content={file.raw_file_content}
                            language={file.raw_file_extension}
                          />
                        </div>
                      ),
                  )
                ) : (
                  <p>No files available</p>
                )}
              </div>
            </div>
          </div>
        </>
      ),
    },
  };

  const checkFilesCompatibility = (files) => {
    const allowedExtensions = getAllowedExtensions();
    return files.filter((file) => {
      const ext = `.${file.name.split(".").pop().toLowerCase()}`;
      return allowedExtensions.includes(ext);
    });
  };

  // Replace your current database selection handler with this:
  const handleDatabaseSelect = (db, isSource = true) => {
    if (isSource) {
      // If clicking the already selected source database, unselect it
      if (selectedSourceDatabase === db) {
        setSelectedSourceDatabase(null);
        return;
      }

      // Check if we're changing to a different source database
      if (uploadedFiles.length > 0) {
        const allowedExtensions = databaseFileTypes[db] || [];
        const hasIncompatibleFiles = uploadedFiles.some((file) => {
          const ext = `.${file.name.split(".").pop().toLowerCase()}`;
          return !allowedExtensions.includes(ext);
        });

        if (hasIncompatibleFiles) {
          const compatibleFiles = uploadedFiles.filter((file) => {
            const ext = `.${file.name.split(".").pop().toLowerCase()}`;
            return allowedExtensions.includes(ext);
          });

          setUploadedFiles(compatibleFiles);
          if (compatibleFiles.length === 0) {
            setUploadComplete(false);
          }
          showToast(`Incompatible files removed for ${db}`, "warning");
        }
      }
      setSelectedSourceDatabase(db);
      // Also reset target database when source changes
      setSelectedTargetDatabase(null);
    } else {
      // If clicking the already selected target database, unselect it
      if (selectedTargetDatabase === db) {
        setSelectedTargetDatabase(null);
        return;
      }
      setSelectedTargetDatabase(db);
    }
  };
  if (!isValidStep(currentStep)) {
    return <div>Invalid step</div>;
  }

  return (
    <div>
      <div
        className={`right-bar3 p-3 ${show ? "d-block" : "d-none"}`}
        style={modalStyle}
      >
        <div>
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="font-size-13 mb-0 fw-medium text-dark">
              Start New Code Conversion
            </h6>
            <CustomTooltip
              content="Close"
              placement="left"
              fallbackPlacements={["bottom"]}
            >
              <i
                onClick={handleClose}
                className="mdi mdi-close close-trace cursor-pointer right-bar-toggle3 iconColor"
              />
            </CustomTooltip>
          </div>
          <div className="trace-label  mt-1 pe-3 d-flex align-items-center mb-3 w-fit-content">
            <img
              src="/assets/images/trace_label.png"
              className="w-14px me-2"
              alt="Job icon"
            />
            <h6 className="font-size-10 mb-0 text-dark">
              Create Job – Set up a new job
            </h6>
          </div>

          <p className="mb-1 mt-2 font-size-12 text-dark">
            Step {currentStep} of 6
          </p>
          <h6 className="fw-bold">{stepInfo[currentStep].title}</h6>
          <p className="font-size-12 text-dark">
            {stepInfo[currentStep].description}
          </p>

          <div className="stepper-container">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div key={step} className="stepper">
                <div
                  className={`step ${
                    currentStep === step
                      ? "active"
                      : currentStep > step
                        ? "completed"
                        : ""
                  }`}
                  data-step={step}
                >
                  {step}
                </div>
                {step < 6 && (
                  <div className="line-container">
                    <div
                      className={`line ${
                        currentStep > step ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div
            id="stepContent"
            style={{
              maxHeight: "calc(100vh - 286px)",
              ...(currentStep === 5 && sourceType === "upload"
                ? {
                    overflowY: "scroll",
                    overflowX: "hidden",
                  }
                : currentStep === 6
                  ? {
                      overflowY: "scroll",
                      overflowX: "hidden",
                    }
                  : {}),
            }}
          >
            {currentStep === 2 || currentStep === 3 ? (
              <>
                {currentStep === 2 && (
                  <>
                    {/* job meta */}
                    <h6 className="fw-sbold font-size-12">Job Name</h6>
                    <p className="font-size-12 text-dark">{jobName || "123"}</p>

                    <h6
                      className="fw-sbold font-size-12"
                      style={{ textAlign: "justify" }}
                    >
                      Job Description
                    </h6>
                    <p className="font-size-12 text-dark">
                      {jobDescription || "N/A"}
                    </p>

                    {/* selector */}
                    <h6 className="fw-sbold font-size-12">
                      Choose Source Database Type
                    </h6>

                    {isLoading ? (
                      <p>Loading databases…</p>
                    ) : (
                      <div
                        className="scrollable-db-grid"
                        ref={dbGridRef}
                        style={{
                          maxHeight: gridMaxHeight
                            ? `${gridMaxHeight}px`
                            : "none",
                          overflowY:
                            gridMaxHeight && gridMaxHeight < 400
                              ? "auto"
                              : "visible",
                        }}
                      >
                        <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-6 g-2  mb-8">
                          {availableSources.map((db) => (
                            <div key={db} className="col">
                              <div
                                className="selectable-card"
                                onClick={() => handleDatabaseSelect(db, true)}
                              >
                                <div className="db-logo-wrapper">
                                  <img
                                    src={getDatabaseImagePath(db)}
                                    onError={(e) => {
                                      console.error(
                                        "Failed to load image:",
                                        e.target.src,
                                      );
                                      e.target.src = getDatabaseImagePath(
                                        db,
                                      ).replace(".png", ".jpg");
                                    }}
                                    alt={db}
                                    style={getCustomImageStyle(db)}
                                    className="db-logo"
                                  />
                                </div>
                                <p className="custom-card-text">{db}</p>

                                {selectedSourceDatabase === db && (
                                  <div className="selected-overlay">
                                    <img
                                      src="assets/images/checkbox.png"
                                      className="selected-check"
                                      alt="Selected"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {currentStep === 3 && (
                  <>
                    <h6 className="fw-sbold font-size-12">Job Name</h6>
                    <p className="font-size-12 text-dark">{jobName || "123"}</p>

                    <h6 className="fw-sbold font-size-12">Job Description</h6>
                    <p className="font-size-12 text-dark">
                      {jobDescription || "N/A"}
                    </p>

                    <h6 className="fw-sbold font-size-12">
                      Type of Source Database
                    </h6>
                    <p className="font-size-12 text-dark">
                      {selectedSourceDatabase || "Not specified"}
                    </p>

                    <h6 className="fw-sbold font-size-12">
                      Choose Target Database
                    </h6>

                    {isLoading ? (
                      <p>Loading databases…</p>
                    ) : (
                      <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-6 g-2 ms-2 mb-2">
                        {(availableTargets[selectedSourceDatabase] || []).map(
                          (db) => (
                            <div key={db} className="col">
                              <div
                                className="selectable-card"
                                // onClick={() => handleDatabaseSelect(db, true)}
                                onClick={() => handleDatabaseSelect(db, false)}
                              >
                                <div className="db-logo-wrapper">
                                  <img
                                    src={getDatabaseImagePath(db)}
                                    onError={(e) => {
                                      console.error(
                                        "Failed to load image:",
                                        e.target.src,
                                      );
                                      e.target.src = getDatabaseImagePath(
                                        db,
                                      ).replace(".png", ".jpg");
                                    }}
                                    alt={db}
                                    style={getCustomImageStyle(db)}
                                    className={`
    db-logo
    ${db && String(db).trim() === "Spark SQL" ? "db-logo-spark-sql" : ""}

  `}
                                  />
                                </div>
                                <p className="custom-card-text">{db}</p>

                                {selectedTargetDatabase === db && (
                                  <div className="selected-overlay">
                                    <img
                                      src="assets/images/checkbox.png"
                                      className="selected-check"
                                      alt="Selected"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              stepInfo[currentStep]?.content
            )}
          </div>
          <div className="position-absolute close-tab">
            <div
              className="d-flex justify-content-between align-items-center w-100"
              style={{ marginBottom: "10px" }}
            >
              {/* Close button - always on the left */}
              <button
                onClick={handleClose}
                className="btn btn-closebtn custom-button"
              >
                Close
              </button>

              {/* Action buttons - right side */}
              <div className="d-flex gap-2" style={{ marginRight: "2px" }}>
                {currentStep === 6 ? (
                  // Step 6 specific buttons
                  <>
                    <button
                      onClick={startExecution}
                      disabled={isExecuting}
                      style={{ marginRight: "-19px" }}
                      className="btn btn-primary custom-button"
                    >
                      {isExecuting ? "Executing" : "Start Execution"}
                    </button>
                  </>
                ) : (
                  // All other steps
                  <>
                    {currentStep > 1 && (
                      <button
                        onClick={() => updateStep(currentStep - 1)}
                        className="btn btn-closebtn custom-button"
                      >
                        Previous
                      </button>
                    )}
                    {Object.entries(getButtonConfig()).map(
                      ([key, config]) =>
                        key === "primary" && (
                          <button
                            key={key}
                            onClick={config.onClick}
                            disabled={config.disabled}
                            className={config.className}
                          >
                            {isProcessingStep &&
                            (config.text === "Next" ||
                              config.text === "Processing...") ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Processing...
                              </>
                            ) : isUploading && config.text === "Upload" ? (
                              "Uploading"
                            ) : (
                              config.text
                            )}
                          </button>
                        ),
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`rightbar-overlay ${show ? "d-block" : "d-none"}`}></div>
    </div>
  );
}

export default CreateModels;
